import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import React from 'react';
import { Link } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses';
import { paths } from '../../common/constants/pathVariables.js';
import { VOICE_PAGE_WRAPPER_ID } from '../../common/constants/subscriptionConstants.js';
import {
  allPricesVatZeroMsg,
  corporateOmaElisaMsg,
  customerServiceNumberHrefMsg,
  customerServiceNumberLabelMsg,
  customerServiceNumberOpenShortMsg,
  customerServiceNumberPriceMsg,
  elisaKukaSoittaaMsg,
  employeeOmaElisaMsg,
  loginToOmaElisaMsg,
  pinCodeMsg,
  pukCodeMsg,
  t,
} from '../../common/i18n';
import { scrollIntoViewById } from '../../common/utils/htmlUtils.js';
import { FaqAccordion } from '../FaqAccordion/FaqAccordion.js';
import { RemoteMeetingIcon } from '../Icons/RemoteMeetingIcon';
import { PAGE_MARGINS_AS_PADDING } from '../PageMargins/PageMargins.js';
import { Picture } from '../Picture/Picture.js';
import { pictureStorageBaseUrl } from '../Picture/contentfulImageProxy.js';
import { BenefitsOf5GSubscription } from './BenefitsOf5GSubscription';
import { HowToOrderASubscription } from './HowToOrderASubscription';
import { InfoBanner } from './InfoBanner.js';
import { PublicSubscriptionImageCards } from './PublicSubscriptionImageCards.js';
import type { JSX } from 'react';

import './PublicMobileVoiceSubscriptions.scss';

const ADDITIONAL_SERVICES_BLOCK_ID = 'yritysliittymat_additional_services';
const FAQ_BLOCK_ID = 'yritysliittymat_faq_accordion';

const MARKETING_BLOCK_IMAGE_RULES = {
  onPhone: 'calc(100vw - 32px)',
  onPhoneLarge: 'calc(100vw - 48px)',
  onLaptop: 'calc((100vw - 64px) * 0.4)',
  onDesktop: '509px',
};

const MARKETING_BLOCK_IMAGE_SIZES: [number, ...number[]] = [288, 340, 433, 448, 509, 515, 592, 1018];

const ScrollToBlockLinkButton = ({ targetId, label }: { targetId: string; label: string }) => (
  <div className={`${dsClass.TEXT_ALIGN_CENTER} ${dsClass.MARGIN_VERTICAL_6}`}>
    <CL.Link
      linkStyle="link-button"
      buttonColor="light"
      buttonSize="l"
      linkHref={`#${targetId}`}
      onClick={(e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        scrollIntoViewById(targetId);
        return false;
      }}
    >
      {label}
    </CL.Link>
  </div>
);

const AdditionalServices = (): JSX.Element => (
  <section id={ADDITIONAL_SERVICES_BLOCK_ID}>
    <div>
      <h2 className={classNames([dsClass.MARGIN_TOP_7, dsClass.MARGIN_BOTTOM_7])}>
        {t.Z7ZV('Additional Services for Business Subscriptions')}
      </h2>
      <PublicSubscriptionImageCards
        items={[
          {
            anchor: 'matkapuhelinvastaaja',
            content: (
              <>
                <p>
                  <Link to="/matkapuhelinvastaaja">{t.SDX4('Elisa Matkapuhelinvastaaja')}</Link>
                  {t.SDX5(
                    ' (Elisa Mobile Phone Voice Mail) adds voicemail, an Away Attendant and notifications for unanswered calls. You are notified of unanswered calls by SMS.'
                  )}
                </p>
                <p>
                  {t.SDX6(
                    'With the Away Attendant, you can also separate work-related and private calls from each other and direct work-related calls to the desired person during your holiday.'
                  )}
                </p>
              </>
            ),
            image: '1tZ63fXJsl17gJ7ycwP7Xp/Answering Machine.svg?w=800',
            imageAlt: 'Vastaaja matkapuhelimeen',
            title: t.SDX3('Mobile voicemail service'),
          },
          {
            anchor: 'mobiiliturva',
            content: (
              <>
                <p>
                  <Link to={t.P6TX('/en/mobile-network-security')}>Elisa Mobiiliturva yrityksille</Link>
                  {t.SE00(
                    ' improves the data security of browsers. It identifies malicious websites and notifies the user.'
                  )}
                </p>
                <p>
                  {t.SE01(
                    'You will have security against the most common data security threat, phishing, when using 4G or 5G internet.'
                  )}
                </p>
              </>
            ),
            image: '7sTxRRCFSB6naE2aMCUNsK/Mobiiliturva.svg?w=800',
            imageAlt: 'Elisa Mobiiliturva yrityksen puhelinliittymään',
            title: t.U57Z('Mobiiliturva blocks malicious websites'),
          },
          {
            anchor: 'kukasoittaa',
            content: (
              <>
                <p>
                  <Link to="/kukasoittaa">{t.AY02(elisaKukaSoittaaMsg)}</Link>
                  {t.SDQA(
                    ' (Who is Calling) number identification service displays the caller’s name on your phone, even if the number isn’t saved in your contacts.'
                  )}
                </p>
                <p>
                  {t.W885(
                    'The caller’s name appears during incoming calls as well as in your call history. From now on, you’ll always know which calls to answer.'
                  )}
                </p>
              </>
            ),
            image: '4HugORABEKdfKZoTzRKj5K/Kuka%20Soittaa.svg?w=64',
            imageAlt: 'Elisa Kuka Soittaa',
            title: t.JCUQ('Identify an incoming call'),
          },
          {
            anchor: 'reissunetti',
            content: (
              <>
                <p>
                  <Link to={t.A564('/en/data-packages')}>{t.SE13('Reissunetti and Reissaaja data packages')}</Link>
                  {t.SDWL(
                    ' also enable carefree internet use outside EU/EEA countries. Our ready-made data packages enable roaming abroad at a lower cost and help you avoid surprising expenses. '
                  )}
                </p>
                <p>
                  {t.SDWP(
                    'The data packages of the subscriptions are activated automatically when you arrive in the country and invoiced separately.'
                  )}
                </p>
              </>
            ),
            image: '3MDQ4FadPjFgmLqoRWv1op/Abroad.svg?w=800',
            title: t.SDWV('Roaming abroad'),
            imageAlt: 'Roaming ulkomailla',
          },
          {
            anchor: 'mobiilivarmenne',
            content: (
              <>
                <p>
                  <Link to="/ohjeet/mobiilivarmenne">{t.T2AE('Elisa Mobiilivarmenne yrityksille')}</Link>
                  {t.X6CL(' provides secure identification for digital services.')}
                </p>
                <p>
                  {t.HEKO(
                    'Registering Mobiilivarmenne for business subscriptions is free of charge and does not include monthly fees.'
                  )}
                </p>
              </>
            ),
            image: <CL.Icon aria-hidden="true" color="black" icon="mobiilivarmenne" size="xl" type="regular" />,
            imageAlt: 'Elisa Mobiilivarmenne yrityksen',
            title: t.X6LZ('Secure identification with Mobiilivarmenne'),
          },
        ]}
        alignLeft={true}
      />
    </div>
  </section>
);

const RememberAlso = (): JSX.Element => (
  <section className="of-public-mobile-voice-subscriptions__remember-also">
    <h3>{t.EKAR('Other Useful Services for Entrepreneurs and Larger Businesses')}</h3>
    <p className={dsClass.TEXT_L}>
      {t.M3GT('Our business subscriptions also support many services that make everyday work easier:')}
    </p>
    <ul className={dsClass.LIST_UNORDERED}>
      <li>
        <Link className={dsClass.LINK} to={paths.OMA_LASKU}>
          Elisa OmaLasku
        </Link>
      </li>
      <li>
        <Link className={dsClass.LINK} to={paths.ESIM_FOR_COMPANIES}>
          Elisa eSIM
        </Link>
      </li>
      <li>
        <Link className={dsClass.LINK} to={paths.WIFI_CALL_SERVICE}>
          Elisa WiFi-puhelinpalvelu
        </Link>
      </li>
      <li>
        <Link className={dsClass.LINK} to={paths.PUHERATKAISU_VAKIO}>
          Elisa Puheratkaisu Vakio
        </Link>
      </li>
      <li>
        <Link className={dsClass.LINK} to={paths.RING_MOBILE_SWITCHBOARD}>
          Elisa Ring -viestintäratkaisu
        </Link>
      </li>
      <li>
        <Link className={dsClass.LINK} to={paths.LANKANUMERO}>
          Elisa Lankapuhelinpalvelu
        </Link>
      </li>
      <li>
        <Link className={dsClass.LINK} to={paths.INTERNET_SUBSCRIPTIONS_PUBLIC}>
          {t.SDXU('Internet subscriptions for companies')}
        </Link>
      </li>
    </ul>
    <p>
      <Link className={dsClass.LINK} to={paths.CUSTOMER_SERVICE_SOLUTIONS}>
        {t.U09E('Explore our comprehensive customer service solutions')}
      </Link>
    </p>
    <div>
      <CL.Link
        linkStyle="link-button"
        buttonColor="light"
        buttonSize="l"
        linkHref={`#${ADDITIONAL_SERVICES_BLOCK_ID}`}
        onClick={(e: React.MouseEvent<HTMLElement>) => {
          e.preventDefault();
          scrollIntoViewById(ADDITIONAL_SERVICES_BLOCK_ID);
          return false;
        }}
      >
        {t.V8YL('Learn more about additional services')}
      </CL.Link>
    </div>
  </section>
);

const LargestCompanyInFinland = (): JSX.Element => (
  <InfoBanner
    title={t.PKER('Are you a member of Suomen Yrittäjät?')}
    description={t.FXJQ(
      'Elisa has been a trusted Finnish partner for entrepreneurs for over 25 years - check out valuable benefits for members!'
    )}
    buttonText={t.QS9H('Explore member benefits')}
    buttonTo={paths.YRITTAJAJASENEN_EDUT}
  />
);

const ElisaMobileNetwork = (): JSX.Element => (
  <section className="of-public-mobile-voice-subscriptions__elisa-mobile-network">
    <div>
      <div>
        <h3>{t.ARE3(`Elisa's Aito 5G network keeps your business running smoothly`)}</h3>
        <p>
          {t.ARE6('Work even from your summer cottage dock and enjoy a reliable connection!')}{' '}
          <Link className={dsClass.LINK} to={paths.COVERAGE}>
            {t.ARE7(`Check your region's 5G coverage on the coverage map.`)}
          </Link>
        </p>
        <p>
          {t.ARE2(
            `Elisa is the perfect choice if you want to support Finnish services - Elisa is the only operator whose mobile and fixed network subscriptions have been awarded the Avainlippu symbol for Finnish service.`
          )}
        </p>
      </div>
      <Picture
        alt="Elisan suomalainen 5G-matkapuhelinverkko"
        offerWidthAlternatives={MARKETING_BLOCK_IMAGE_SIZES}
        renderedImageSize={MARKETING_BLOCK_IMAGE_RULES}
        src={`${pictureStorageBaseUrl}7C6x5QnfW9XPAhG1LP8gL0/i.png`}
      />
    </div>
  </section>
);

const FrequentlyAskedQuestions = () => (
  <FaqAccordion
    heading={t.ARE9('Corporate subscriptions: frequently asked questions')}
    id={FAQ_BLOCK_ID}
    items={[
      {
        content: (
          <ul className={dsClass.LIST_UNORDERED}>
            <li>
              {t.F3W3(
                'Aito 5G is the most advanced network technology available. In the Aito 5G network, data traffic runs entirely on 5G without relying on 4G. This provides a more stable and consistent connection, as devices no longer need to switch continuously between 4G and 5G networks.'
              )}
            </li>
            <li>
              {t.XD7C(
                'You can identify Aito 5G subscriptions for businesses by the 5G+ label displayed on the product cards of eligible business mobile subscriptions.'
              )}
            </li>
            <li>
              {t.F56Z('To get the most out of your Aito 5G subscription, you need ')}{' '}
              <a href={paths.DEVICES_5G_SUPPORT}>
                {t.S8ON('a device that supports 5G Standalone (5G SA) technology.')}
              </a>{' '}
              {t.K2SS(
                ' Aito 5G works automatically on compatible devices and will switch to the 4G network when a 5G connection is not available.'
              )}
            </li>
          </ul>
        ),
        heading: t.DHD6('What does Aito 5G (5G+) mean?'),
      },
      {
        content: (
          <ul className={dsClass.LIST_UNORDERED}>
            <li>
              {t.E68G(
                'For business users, the benefits of Aito 5G include improved performance, lower latency, and fewer connection interruptions in situations such as remote work, video meetings, and cloud service usage.'
              )}
            </li>
            <li>
              {t.K6IL(
                'Aito 5G also provides better performance in congested network conditions. In addition, battery life is improved because the device only needs to use one network instead of two. Compared to traditional 4G or non-standalone 5G subscriptions, battery life can be up to 20% longer.'
              )}
            </li>
          </ul>
        ),
        heading: t.KIB1('What are the benefits of Aito 5G for business subscriptions?'),
      },
      {
        content: (
          <ul className={dsClass.LIST_UNORDERED}>
            <li>
              {t.AREF(
                'The subscriptions also work in the 4G network. When you go outside the 5G area with the Elisa Yritysliittymä 5G+ or 5G subscription, you have access to the 4G network.'
              )}
            </li>
            <li>
              {t.AREG('If your phone only supports the 4G network, your subscription will also use the 4G network.')}{' '}
              <a href={paths.COVERAGE}>{t.ARE8('Check coverage in your area.')}</a>
            </li>
          </ul>
        ),
        heading: t.AREE('Will 5G+ and 5G subscriptions work on 4G?'),
      },
      {
        content: (
          <ul className={dsClass.LIST_UNORDERED}>
            <li>
              {t.AREI('You can order an Elisa corporate subscription when you have the ')}
              <b>{t.AREJ('authority to sign')}</b>
              {t.AREK(' for your company')}.
            </li>
            <li>
              {t.AREL('You need the Corporate OmaElisa login to place an order. If you do not have an account yet, ')}
              <Link to={paths.REGISTER_PAGE}>{t.AREM('create new credentials')}</Link>.
            </li>
          </ul>
        ),
        heading: t.AREH('Who can order a corporate subscription?'),
      },
      {
        content: (
          <>
            <b>{t.AREO('Ordering a new corporate subscription:')}</b>
            <ul className={dsClass.LIST_UNORDERED}>
              <li>
                {t.AREP('Order a new phone subscription for your company on this page or in ')}
                <Link to={paths.SELF_SERVICE_HOME}>{t.AREA(corporateOmaElisaMsg)}</Link>
                {t.AREQ(
                  '. You can order a subscription with a new number or transfer your old number to a new subscription.'
                )}
              </li>
            </ul>
            <b>{t.ARER('To update the current subscription to another subscription package:')}</b>
            <ul className={dsClass.LIST_UNORDERED}>
              <li>
                {t.AREC('Log in to Corporate OmaElisa')}
                {t.ARES(', where you can update your current Elisa phone subscription.')}
              </li>
            </ul>
          </>
        ),
        heading: t.AREN('How do you order a corporate subscription?'),
      },
      {
        content: (
          <ul className={dsClass.LIST_UNORDERED}>
            <li>
              {t.AREU(
                'Elisa Yritysliittymä subscriptions include unlimited data in Sweden, Norway, Denmark, Estonia, Latvia and Lithuania.'
              )}
            </li>
            <li>
              {t.AREV('In addition, each subscription package includes plenty of data in the EU and EEA countries.')}
            </li>
            <li>
              {t.AREW('The administrator can check the subscription’s EU and EEA data volume in ')}
              <Link to={paths.SELF_SERVICE_HOME}>{t.AREB(corporateOmaElisaMsg)}</Link>
              {t.AREX(', and the employee can see it in ')}
              <Link to={paths.EMPLOYEE_HOME}>{t.ARED(employeeOmaElisaMsg)}</Link>.
            </li>
            <li>
              {t.AREY(
                'In other countries, the price varies depending on the destination country. In addition, you can get an inexpensive Reissaaja data package for especially distant countries.'
              )}
            </li>
            <li>
              <Link to={paths.USING_YOUR_PHONE_ABROAD}>{t.AREZ('Read more about using your phone abroad')}</Link>
            </li>
          </ul>
        ),
        heading: t.ARET('How much data can I use abroad?'),
      },
      {
        content: (
          <>
            <ul className={dsClass.LIST_UNORDERED}>
              <li>
                {t.ARF1(
                  'You can keep your current number when you change your mobile subscription to Elisa. During the ordering process, simply select '
                )}
                <b>{t.ARF2('“Keep your number”')}</b>.
              </li>
            </ul>
            <b>{t.ARF3('Do I have to terminate my old subscription myself?')}</b>
            <br />
            <ul className={dsClass.LIST_UNORDERED}>
              <li>
                {t.ARF4(
                  'When you switch over to Elisa from another operator, you do not need to contact your old operator. We will take care of transferring the subscription to Elisa on your behalf.'
                )}
              </li>
            </ul>
          </>
        ),
        heading: t.ARF0('Can I keep my current number if I transfer my subscription to Elisa?'),
      },
      {
        content: (
          <p>
            {t.ARF6('Read instructions for ')}
            <Link to={paths.INSTRUCTION_CHANGE_SUBSCRIPTION_OWNERSHIP}>
              {t.ARF7('changing the ownership of a subscription')}
            </Link>
            .
          </p>
        ),
        heading: t.ARF5('How do I change my subscription ownership?'),
      },
      {
        content: (
          <ul className={dsClass.LIST_UNORDERED}>
            <li>{t.ARF9('After placing an order, you will receive an order confirmation.')}</li>
            <li>
              {t.ARFA('We will deliver the SIM card to you in advance and send the ')}
              <b>{t.ARFB('exact transfer time')}</b>
              {t.ARFC(
                ' to the user’s phone. The message will inform you when the new subscription is functional and the SIM card can be changed to the phone.'
              )}
            </li>
            <li>
              {t.ARFD(
                'If you have changed your subscription from another operator to Elisa, you do not need to contact your old operator. We will take care of the transfer on your behalf.'
              )}
            </li>
            <li>
              <Link to={paths.SUBSCRIPTION_ACTIVATION}>
                {t.ARFE('Read the instructions for deploying the subscription ')}
              </Link>
            </li>
            <li>
              {t.ARFF('If you have any questions, you can also contact ')}
              <Link to={paths.CONTACT_INFO}>{t.ARFG('our customer service')}</Link>.
            </li>
          </ul>
        ),
        heading: t.ARF8('How do I get access to a telephone subscription?'),
      },
      {
        content: (
          <>
            <div className={dsClass.PADDING_BOTTOM_4}>
              <b>{t.ALQT(pinCodeMsg)}</b>
              <ul className={dsClass.LIST_UNORDERED}>
                <li>{t.ARFI('The default PIN code for Elisa’s corporate subscriptions is 1234.')}</li>
                <li>{t.ARFJ('You can change the PIN code to another one in the phone’s settings.')}</li>
              </ul>
            </div>
            <div className={dsClass.PADDING_BOTTOM_4}>
              <b>{t.K5TL(pukCodeMsg)}</b>
              <ul className={dsClass.LIST_UNORDERED}>
                <li>
                  {t.ARFK('If you are an employee, you can find your PUK code in ')}
                  <Link to={paths.EMPLOYEE_HOME}>{t.ARED(employeeOmaElisaMsg)}</Link>.
                </li>
                <li>
                  {t.ARFL(
                    'If you are an entrepreneur or a person with the authority to sign for your company, you can find the PUK code in '
                  )}
                  <Link to={paths.SELF_SERVICE_HOME}>{t.AREB(corporateOmaElisaMsg)}</Link>.
                </li>
              </ul>
            </div>
            <p>
              <Link to={paths.INSTRUCTION_PIN_AND_PUK_CODES}>
                {t.ARFM('Read the instructions for the PIN and PUK codes.')}
              </Link>
            </p>
          </>
        ),
        heading: t.ARFH('Where can I find the PIN and PUK code?'),
      },
      {
        content: (
          <>
            <div className={dsClass.PADDING_BOTTOM_4}>
              <b>{t.QQGQ('Ordering SIM cards')}</b>
              <p>
                {t.ARFO('You can order ')}
                <Link to={paths.ELISA_SIM_CARD_PRODUCT}>{t.ARFP('new SIM cards online')}</Link> {t.ARFQ('or in')}{' '}
                <Link to={paths.SELF_SERVICE_HOME}>{t.AREB(corporateOmaElisaMsg)}</Link>.
              </p>
            </div>
            <div className={dsClass.PADDING_BOTTOM_4}>
              <b>{t.XUME('Activating SIM cards')}</b>
              <p>{t.ARFT('The SIM card can be activated by the company’s administrator or employee.')}</p>
            </div>
            <p>
              {t.ARFR('Read instructions for ')}
              <Link to={paths.INSTRUCTION_SIM_CARD_ORDERING_AND_ACTIVATION}>
                {t.ARFS('ordering and managing SIM cards')}
              </Link>
              .
            </p>
          </>
        ),
        heading: t.ARFN('How do I order and activate new SIM cards?'),
      },
      {
        content: (
          <>
            <p>
              {t.SF10(
                'For now, the standalone 5G network works with a limited number of smartphones, but the selection is growing regularly. Check out our listing of '
              )}
              <Link to="/kauppa/itsenaisen-verkon-laitteet">{t.SF11('devices with 5G+ support')}</Link>.
            </p>
            <p>
              {t.SF12(
                'It is not always necessary to change your phone, as for many newer devices it is enough to keep up to date with software updates.'
              )}
            </p>
          </>
        ),
        heading: t.SF09('Which phones work on a standalone network?'),
      },
      {
        content: (
          <p>
            {t.SF14(
              'A subscription does not update automatically. You can update your phone subscription by logging in to '
            )}
            <Link to={paths.SELF_SERVICE_HOME}>{t.SF15(corporateOmaElisaMsg)}</Link>
            {'. '}
            {t.SF16('If you need more information, ')}
            <Link to={paths.CONTACT_INFO}>{t.SF17('our customer service team will be happy to help')}</Link>.
          </p>
        ),
        heading: t.SF13(
          'My business subscription is without the + feature. Will my current plan update automatically?'
        ),
      },
      {
        content: (
          <p>
            {t.SF19(
              'Yes. If your phone doesn’t support a standalone network, it will use a standard 5G or 4G connection just like before.'
            )}
          </p>
        ),
        heading: t.SF18('Can I use 5G+ on a phone that doesn’t support a standalone network?'),
      },
    ]}
  />
);

const Contact = (): JSX.Element => (
  <section className={`of-public-mobile-voice-subscriptions__contact ${PAGE_MARGINS_AS_PADDING}`}>
    {/* TODO use new CMS Templates' ContactGrid when it's actually possible to use without importing the CSS twice. */}
    <h2>{t.SDWH('Do you have any questions about corporate telephone subscriptions?')}</h2>
    <div>
      <div>
        <CL.Icon aria-hidden="true" color="neutral-600" icon="smartphone" size="xl" type="regular" />
        <h3>{t.JXQZ('Sales service')}</h3>
        <a
          className={`${dsClass.LINK} ${dsClass.LINK_BUTTON_COLOR_LIGHT} ${dsClass.LINK_BUTTON_SIZE_L} ${dsClass.LINK_STYLE_LINK_BUTTON}`}
          href="tel:+35880004411"
        >
          0800 04411
        </a>
        <p className={dsClass.TEXT_S}>{t.SDUB('Mon-Fri 8am-4pm | Free of charge')}</p>
      </div>
      <div>
        <CL.Icon aria-hidden="true" color="neutral-600" icon="smartphone" size="xl" type="regular" />
        <h3>{t.L6NC('Call corporate customer service')}</h3>
        <a
          className={`${dsClass.LINK} ${dsClass.LINK_BUTTON_COLOR_LIGHT} ${dsClass.LINK_BUTTON_SIZE_L} ${dsClass.LINK_STYLE_LINK_BUTTON}`}
          href={customerServiceNumberHrefMsg}
        >
          {customerServiceNumberLabelMsg}
        </a>
        <p className={dsClass.TEXT_S}>
          {t.HQWU(customerServiceNumberOpenShortMsg)} | {t.HQWW(customerServiceNumberPriceMsg)}
        </p>
      </div>
      <div>
        <RemoteMeetingIcon />
        <h3>{t.D2W9('Remote meeting with corporate sales')}</h3>
        <Link
          className={`${dsClass.LINK} ${dsClass.LINK_BUTTON_COLOR_LIGHT} ${dsClass.LINK_BUTTON_SIZE_L} ${dsClass.LINK_STYLE_LINK_BUTTON}`}
          to={paths.STORES}
        >
          {t.D2W8('Book appointment')}
        </Link>
      </div>
    </div>
  </section>
);

const Disclaimer = () => (
  <section>
    <div>
      <h3 className={`${dsClass.H3_BOOK} ${dsClass.PADDING_BOTTOM_0}`}>{t.TEA3(allPricesVatZeroMsg)}</h3>
      <p className={`${dsClass.TEXT_S} ${dsClass.TEXT_ALIGN_LEFT}`}>
        {t.SDXO(
          'Data transfer rates vary depending on the properties, the location of the device and the network load. Check your area’s coverage on the '
        )}
        <a href={paths.COVERAGE}>{t.NQYM('coverage map')}</a>
        {t.SDXQ(
          '. Nordic countries (except Iceland) and Baltic countries: Sweden, Norway, Denmark, Estonia, Latvia and Lithuania. In Yritysliittymä 5G+ and 5G subscriptions, data exceeding the usage limit is priced according to the '
        )}
        <Link to={paths.USING_YOUR_PHONE_ABROAD}>{t.SDXR('roaming price list')}</Link>
        {t.SDXS(
          '. 5G+ subscriptions include 5,000 minutes of voice calls per month to normal-priced Finnish mobile and landline numbers and 100 SMS messages per month. The package does not cover international calls or calls to service numbers subject to an extra fee, such as those with 0700 or 0600 prefixes. When calling abroad or service numbers subject to an extra fee, a mobile network charge (mpc) will also be charged according to the current price list. The prices do not apply to networks subject to special prices (ships, satellites and aircraft).'
        )}
      </p>
    </div>
  </section>
);

const BackToSubscriptions = () => (
  <ScrollToBlockLinkButton targetId={VOICE_PAGE_WRAPPER_ID} label={t.SDXT('Back to the top')} />
);

export const PublicMobileVoiceSubscriptionsAdditionalComponents = () => {
  return (
    <>
      <HowToOrderASubscription />
      <BenefitsOf5GSubscription />
      <AdditionalServices />
      <RememberAlso />
      <InfoBanner
        title={t.ELJB('Are you already an Elisa customer?')}
        description={t.YV0E(
          'In Corporate OmaElisa, you can manage your current subscriptions, review your invoices and order new subscriptions.'
        )}
        buttonText={t.JOM3(loginToOmaElisaMsg)}
        buttonTo={paths.SELF_SERVICE_HOME}
      />
      <ElisaMobileNetwork />
      <LargestCompanyInFinland />
      <div id="GIOSG_CONTENT_PLACEHOLDER"></div>
      <FrequentlyAskedQuestions />
      <Contact />
      <Disclaimer />
      <BackToSubscriptions />
    </>
  );
};
